
import type {
  CreatePilotResponse,
  Dictamen,
  HelpType,
  PilotInput,
  PilotState,
  SessionListItem,
  TurnResponse,
} from './types';
// Tipos del API client viven en api-types.ts (extraídos 2026-07-31). Los
// re-exportamos acá para que los ~40 imports actuales que hacen
// `import { ... } from './api'` no rompan.
export type * from './api-types';
// Imports internos (los handlers los usan directo):
import type {
  ClassroomAnalyticsResponse,
  ClassroomDetail,
  ClassroomLive,
  ClassroomListItem,
  ClassroomStreamEvent,
  CreateClassroomInput,
  ScoreBreakdownResponse,
  CreateClassroomResponse,
  EnrichmentDetail,
  EnrichmentRecord,
  EnrichmentStatus,
  EnrichmentTipo,
  LearnerProfile,
  LearnerSearchResponse,
  MetricsSummary,
  PionerosLearningObjective,
  PionerosLevel,
  PionerosMilestone,
  PionerosSubject,
  RosterLearner,
  TeacherReviewRecord,
  AudioRef,
  AuditQueryOpts,
  AuditHourBucket,
  AuditUserBucket,
  AuditNamespaceBucket,
  AuditLatencyBucket,
  AuditRow,
} from './api-types';
import type {
  LessonMaterialMime,
  LessonMaterialPreviewRequest,
  LessonMaterialPreviewResponse,
  LessonMaterialUploadUrlResponse,
  RemedialTaskResponse,
  RemedialUploadMime,
} from '@marzano/contracts';

// Convierte Blob de audio a base64 (los routes /turn-audio esperan el string
// listo en el body JSON, no multipart). Vive acá en el mismo file que el
// caller (`submitTurnAudio`) por proximidad.
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') return reject(new Error('FileReader sin resultado'));
      // FileReader devuelve "data:audio/webm;base64,...": sacamos el header.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}

let accessTokenProvider: (() => Promise<string | null>) | null = null;
let siteIdProvider: (() => string | null) | null = null;
// Link-token del flujo "Aulas": el alumno entra sin MSAL. Si está seteado,
// el request agrega el header X-Link-Token; el backend prioriza esa vía
// sobre Bearer AD en rutas `roleCheckOrLinkToken`.
let linkTokenProvider: (() => string | null) | null = null;

export function setAccessTokenProvider(fn: (() => Promise<string | null>) | null): void {
  accessTokenProvider = fn;
}

export function setSiteIdProvider(fn: (() => string | null) | null): void {
  siteIdProvider = fn;
}

export function setLinkTokenProvider(fn: (() => string | null) | null): void {
  linkTokenProvider = fn;
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (json !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (linkTokenProvider && !headers.has('X-Link-Token')) {
    const linkTok = linkTokenProvider();
    if (linkTok) headers.set('X-Link-Token', linkTok);
  }
  if (accessTokenProvider && !headers.has('Authorization')) {
    const token = await accessTokenProvider();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  if (siteIdProvider && !headers.has('X-Site-Id')) {
    const sid = siteIdProvider();
    if (sid !== null) headers.set('X-Site-Id', sid);
  }
  const resp = await fetch(path, {
    ...rest,
    headers,
    body: json !== undefined ? JSON.stringify(json) : (rest.body ?? null),
  });
  const text = await resp.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // no JSON; parsed queda null
  }
  if (!resp.ok) {
    const err = parsed as
      | {
          title?: string;
          detail?: string;
          code?: string;
          meta?: { issues?: Array<{ path?: unknown; message: string }> };
        }
      | null;
    // Resumir el primer issue de Zod si existe.
    let msg = err?.detail || err?.title || resp.statusText;
    const issues = err?.meta?.issues;
    if (Array.isArray(issues) && issues.length > 0) {
      const first = issues[0]!;
      // Zod manda path como array, pero aceptamos string suelto por defensividad.
      let pathStr: string | null = null;
      if (Array.isArray(first.path) && first.path.length > 0) {
        pathStr = first.path.join('.');
      } else if (typeof first.path === 'string' && first.path.length > 0) {
        pathStr = first.path;
      }
      msg = pathStr ? `${pathStr}: ${first.message}` : first.message;
    }
    throw new ApiError(
      msg,
      resp.status,
      err?.code ?? null,
      (issues as Array<{ path?: string[]; message: string }> | undefined) ?? null,
    );
  }
  return parsed as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null,
    public readonly issues: Array<{ path?: string[]; message: string }> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}


async function parseSseStream(
  path: string,
  body: unknown,
  handlers: {
    onDim?: (data: { dim: string; latency_ms: number; evidencias_count: number; preguntas_count: number }) => void;
    onComplete?: (data: { enrichment: EnrichmentRecord; latency_ms_total: number; tokens_total: number }) => void;
    onError?: (msg: string) => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessTokenProvider) {
    const token = await accessTokenProvider();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (siteIdProvider) {
    const sid = siteIdProvider();
    if (sid !== null) headers['X-Site-Id'] = sid;
  }
  const resp = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal: signal ?? null,
  });
  if (!resp.ok || !resp.body) {
    const text = await resp.text();
    handlers.onError?.(`stream HTTP ${resp.status}: ${text.slice(0, 200)}`);
    return;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    if (signal?.aborted) {
      reader.cancel().catch(() => undefined);
      return;
    }
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sepIdx: number;
    while ((sepIdx = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, sepIdx);
      buf = buf.slice(sepIdx + 2);
      let event = 'message';
      let data = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data) continue;
      try {
        const parsed = JSON.parse(data);
        if (event === 'dim_ready') handlers.onDim?.(parsed);
        else if (event === 'complete') handlers.onComplete?.(parsed);
        else if (event === 'error') handlers.onError?.(parsed.message ?? 'stream error');
      } catch {
        // chunk malformado; ignorar
      }
    }
  }
}

export const api = {
  createPilot(input: PilotInput): Promise<CreatePilotResponse> {
    return request('/v1/pilots', { method: 'POST', json: input });
  },
  startInterview(
    sessionId: string,
    siteId: string,
  ): Promise<{ question: string; current_dim: string }> {
    return request(`/v1/pilots/${sessionId}/start`, {
      method: 'POST',
      json: { site_id: siteId },
    });
  },
  submitTurnText(
    sessionId: string,
    siteId: string,
    answer: string,
    secondsUsed: number,
    answerMetadata?: { paste_attempts: number; answer_time_ms: number },
  ): Promise<TurnResponse> {
    return request(`/v1/pilots/${sessionId}/turn`, {
      method: 'POST',
      json: {
        site_id: siteId,
        answer_text: answer,
        channel_seconds_used: secondsUsed,
        ...(answerMetadata && { answer_metadata: answerMetadata }),
      },
    });
  },
  submitTurnAudio(
    sessionId: string,
    siteId: string,
    audioBlob: Blob,
    durationSec: number,
  ): Promise<TurnResponse> {
    return blobToBase64(audioBlob).then((audio_base64) =>
      request(`/v1/pilots/${sessionId}/turn-audio`, {
        method: 'POST',
        json: {
          site_id: siteId,
          audio_base64,
          mime_type: audioBlob.type || 'audio/webm',
          duration_sec_client: durationSec,
        },
      }),
    );
  },
  registerHelp(
    sessionId: string,
    siteId: string,
    helpType: HelpType,
  ): Promise<{
    ok: true;
    penalty_pct: number;
    help_text: string;
    replacement_question: string | null;
    occurred_at: string;
  }> {
    return request(`/v1/pilots/${sessionId}/help`, {
      method: 'POST',
      json: { site_id: siteId, help_type: helpType },
    });
  },
  closePilot(
    sessionId: string,
    siteId: string,
    reason: 'teacher_early_close' | 'abandoned' | 'time_exhausted' = 'teacher_early_close',
  ): Promise<{ dictamen: Dictamen }> {
    return request(`/v1/pilots/${sessionId}/close`, {
      method: 'POST',
      json: { site_id: siteId, reason },
    });
  },
  getPilot(sessionId: string, siteId: string): Promise<PilotState> {
    return request(`/v1/pilots/${sessionId}?site_id=${siteId}`);
  },
  getBreakdown(sessionId: string): Promise<ScoreBreakdownResponse> {
    return request(`/v1/pilots/${sessionId}/breakdown`);
  },
  getAudioRefs(
    sessionId: string,
    siteId: string,
  ): Promise<{ audios: AudioRef[] }> {
    return request(`/v1/pilots/${sessionId}/audio-refs?site_id=${siteId}`);
  },
  submitReview(
    sessionId: string,
    siteId: string,
    review: {
      decision: 'accepted' | 'modified' | 'rerun_requested';
      observations?: string | null;
      overrides?: Record<string, unknown> | null;
      intervened_during_session?: boolean;
      intervention_counts_as_help?: boolean | null;
      teacher_email?: string;
    },
  ): Promise<{ review: TeacherReviewRecord }> {
    return request(`/v1/pilots/${sessionId}/review`, {
      method: 'POST',
      json: { site_id: siteId, ...review },
    });
  },
  getReviews(
    sessionId: string,
    siteId: string,
  ): Promise<{ reviews: TeacherReviewRecord[] }> {
    return request(`/v1/pilots/${sessionId}/reviews?site_id=${siteId}`);
  },
  getDashboardMe(): Promise<{
    role: string;
    has_learner_profile: boolean;
    learner_id?: string;
    dim_averages?: {
      recuperacion: number;
      comprension: number;
      analisis: number;
      transferencia: number;
      global: number;
      n_pilots: number;
    };
    sessions?: Array<{
      session_id: string;
      mongo_oa_id: string;
      modality: string;
      started_at: string;
      closed_at: string | null;
      current_state: string;
      oa_codigo: string | null;
      oa_descripcion: string | null;
      global_score: number | null;
      state_label: string | null;
    }>;
    next_cursor?: string | null;
    score_evolution?: Array<{ date: string; global_score: number; state: string }>;
  }> {
    return request('/v1/dashboard/me');
  },
  getDashboardLearner(learnerId: string, cursor?: string) {
    const p = new URLSearchParams({ learner_id: learnerId });
    if (cursor) p.set('cursor_started_at', cursor);
    return request<{
      learner_id: string;
      dim_averages: {
        recuperacion: number;
        comprension: number;
        analisis: number;
        transferencia: number;
        global: number;
        n_pilots: number;
      };
      sessions: Array<{
        session_id: string;
        mongo_oa_id: string;
        modality: string;
        started_at: string;
        closed_at: string | null;
        current_state: string;
        oa_codigo: string | null;
        oa_descripcion: string | null;
        global_score: number | null;
        state_label: string | null;
      }>;
      next_cursor: string | null;
      score_evolution: Array<{ date: string; global_score: number; state: string }>;
    }>(`/v1/dashboard/learner?${p.toString()}`);
  },
  searchLearnersAdmin(opts: { q?: string; level_name?: string; cursor?: string }) {
    const p = new URLSearchParams();
    if (opts.q) p.set('q', opts.q);
    if (opts.level_name) p.set('level_name', opts.level_name);
    if (opts.cursor) p.set('cursor', opts.cursor);
    return request<{
      learners: Array<{
        mongo_learner_id: string;
        mongo_user_id: string | null;
        name: string | null;
        email: string | null;
        level_label: string | null;
        site_id: string;
        n_pilots: number;
        last_pilot_at: string | null;
      }>;
      next_cursor: string | null;
    }>(`/v1/dashboard/learners-search?${p.toString()}`);
  },
  getDashboardLevels() {
    return request<{ levels: string[] }>('/v1/dashboard/levels');
  },
  listPrompts() {
    return request<{
      prompts: Array<{
        engine: string;
        name: string;
        active_version_id: number | null;
        active_version: string | null;
        active_body_preview: string | null;
        active_updated_at: string | null;
        active_updated_by: string | null;
        total_versions: number;
      }>;
    }>('/v1/admin/prompts');
  },
  listPromptVersions(engine: string, name: string) {
    return request<{
      versions: Array<{
        id: number;
        engine: string;
        name: string;
        version: string;
        body: string;
        notes: string | null;
        is_active: boolean;
        published_at: string;
        retired_at: string | null;
        created_by_email: string | null;
      }>;
    }>(`/v1/admin/prompts/${engine}/${name}/versions`);
  },
  createPromptVersion(engine: string, name: string, body: { body: string; notes?: string | null }) {
    return request<{
      version: {
        id: number;
        engine: string;
        name: string;
        version: string;
        body: string;
        notes: string | null;
        is_active: boolean;
        published_at: string;
      };
    }>(`/v1/admin/prompts/${engine}/${name}/versions`, { method: 'POST', json: body });
  },
  activatePromptVersion(versionId: number) {
    return request<{ version: { id: number; is_active: boolean } }>(
      `/v1/admin/prompts/versions/${versionId}/activate`,
      { method: 'POST', json: {} },
    );
  },
  deactivatePrompt(engine: string, name: string) {
    return request<{ ok: boolean }>(`/v1/admin/prompts/${engine}/${name}/deactivate`, {
      method: 'POST',
      json: {},
    });
  },
  getMetrics(siteId: string, days = 30): Promise<MetricsSummary> {
    return request(`/v1/admin/metrics?site_id=${siteId}&days=${days}`);
  },

  getAuditVolumeByHour(opts: AuditQueryOpts): Promise<{ buckets: AuditHourBucket[] }> {
    return request(`/v1/admin/audit/volume-by-hour?${buildAuditQuery(opts)}`);
  },
  getAuditTopUsers(
    opts: AuditQueryOpts & { limit?: number },
  ): Promise<{ users: AuditUserBucket[] }> {
    return request(`/v1/admin/audit/top-users?${buildAuditQuery(opts)}`);
  },
  getAuditTopNamespaces(
    opts: AuditQueryOpts & { limit?: number },
  ): Promise<{ namespaces: AuditNamespaceBucket[] }> {
    return request(`/v1/admin/audit/top-namespaces?${buildAuditQuery(opts)}`);
  },
  getAuditLatencyHistogram(
    opts: AuditQueryOpts,
  ): Promise<{ histogram: AuditLatencyBucket[] }> {
    return request(`/v1/admin/audit/latency-histogram?${buildAuditQuery(opts)}`);
  },
  getAuditRows(
    opts: AuditQueryOpts & { limit?: number; cursor?: number },
  ): Promise<{ items: AuditRow[]; next_cursor: number | null }> {
    return request(`/v1/admin/audit/rows?${buildAuditQuery(opts)}`);
  },
  getConcordance(
    siteId: string,
    days = 30,
  ): Promise<import('./types').ConcordanceMetrics> {
    return request(`/v1/admin/concordancia?site_id=${siteId}&days=${days}`);
  },
  listEnrichments(opts: {
    status?: EnrichmentStatus;
    asignatura?: string;
    nivel_academico?: string;
    oa_codigo?: string;
    q?: string;
    cursor?: number;
    limit?: number;
  } = {}): Promise<{
    items: Array<{
      id: number;
      mongo_oa_id: string;
      version_id: number;
      aprendizaje_central: string;
      tipo: EnrichmentTipo;
      status: EnrichmentStatus;
      asignatura: string | null;
      nivel_academico: string | null;
      oa_codigo: string | null;
      generated_by_ai_at: string;
      approved_at: string | null;
      created_at: string;
    }>;
    next_cursor: number | null;
  }> {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.asignatura) p.set('asignatura', opts.asignatura);
    if (opts.nivel_academico) p.set('nivel_academico', opts.nivel_academico);
    if (opts.oa_codigo) p.set('oa_codigo', opts.oa_codigo);
    if (opts.q) p.set('q', opts.q);
    if (opts.cursor) p.set('cursor', String(opts.cursor));
    if (opts.limit) p.set('limit', String(opts.limit));
    return request(`/v1/admin/oa-enrichments?${p.toString()}`);
  },
  getEnrichmentFacets(): Promise<{ asignaturas: string[]; niveles: string[] }> {
    return request('/v1/admin/oa-enrichments/facets');
  },
  getEnrichmentDetail(id: number): Promise<EnrichmentDetail> {
    return request(`/v1/admin/oa-enrichments/${id}`);
  },
  createEnrichment(
    siteId: string,
    body: {
      oa_codigo: string;
      oa_descripcion: string;
      asignatura: string;
      nivel_academico: string;
      hito_nombre: string;
      hito_descripcion: string;
      tipo: EnrichmentTipo;
    },
  ): Promise<{ enrichment: EnrichmentRecord }> {
    return request('/v1/admin/oa-enrichments', {
      method: 'POST',
      json: { site_id: siteId, ...body },
    });
  },
  // SSE: emite `dim_ready` por dim y `complete` con el enrichment; AbortSignal cancela stream + calls Azure.
  streamEnrichment(
    siteId: string,
    body: {
      oa_codigo: string;
      oa_descripcion: string;
      asignatura: string;
      nivel_academico: string;
      hito_nombre: string;
      hito_descripcion: string;
      tipo: EnrichmentTipo;
    },
    handlers: {
      onDim?: (data: { dim: string; latency_ms: number; evidencias_count: number; preguntas_count: number }) => void;
      onComplete?: (data: { enrichment: EnrichmentRecord; latency_ms_total: number; tokens_total: number }) => void;
      onError?: (msg: string) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    return parseSseStream('/v1/admin/oa-enrichments/stream', { site_id: siteId, ...body }, handlers, signal);
  },
  listSubjects(
    siteId: string,
    levelName: string,
  ): Promise<{ subjects: PionerosSubject[] }> {
    // site_id se omite si es vacío — el endpoint lo ignora igual (usa identity),
    // pero si mandamos `?site_id=` el schema Zod (`.coerce.number().positive()`)
    // castea a NaN y devuelve 400.
    const p = new URLSearchParams({ level_name: levelName });
    if (siteId) p.set('site_id', siteId);
    return request(`/v1/curriculum/subjects?${p.toString()}`);
  },
  listOAs(
    siteId: string,
    levelName: string,
    signatureId: string,
  ): Promise<{ oas: PionerosLearningObjective[] }> {
    const p = new URLSearchParams({
      level_name: levelName,
      signature_id: signatureId,
    });
    if (siteId) p.set('site_id', siteId);
    return request(`/v1/curriculum/oas?${p.toString()}`);
  },
  listMilestones(
    siteId: string,
    levelMongoId: string,
    signatureId: string,
  ): Promise<{ milestones: PionerosMilestone[] }> {
    const p = new URLSearchParams({
      level_id: levelMongoId,
      signature_id: signatureId,
    });
    if (siteId) p.set('site_id', siteId);
    return request(`/v1/curriculum/milestones?${p.toString()}`);
  },
  listLevels(): Promise<{ levels: PionerosLevel[] }> {
    // Backend saca el site del identity — no hay query params.
    return request('/v1/curriculum/levels');
  },
  searchLearners(q: string, siteId: string): Promise<LearnerSearchResponse> {
    const p = new URLSearchParams({ q, site_id: siteId });
    return request(`/v1/learner/search?${p.toString()}`);
  },
  getLearnerProfile(
    email: string,
    siteId: string,
  ): Promise<LearnerProfile> {
    const p = new URLSearchParams({ email, site_id: siteId });
    return request(`/v1/learner/me?${p.toString()}`);
  },
  getLearnerMarzanoSummary(
    email: string,
    siteId: string,
  ): Promise<{ summary: import('./types').LearnerMarzanoSummary }> {
    const p = new URLSearchParams({ email, site_id: siteId });
    return request(`/v1/learner/marzano-summary?${p.toString()}`);
  },
  approveEnrichment(
    siteId: string,
    id: number,
    approvedByEmail?: string,
  ): Promise<{ ok: true }> {
    return request(`/v1/admin/oa-enrichments/${id}/approve`, {
      method: 'POST',
      json: { site_id: siteId, ...(approvedByEmail && { approved_by_email: approvedByEmail }) },
    });
  },
  listPilots(
    siteId: string,
    opts: {
      limit?: number;
      offset?: number;
      modality?: 'written' | 'oral' | 'mixed' | 'simplified_1_3';
      status?: 'open' | 'closed' | 'reviewed';
      oa_q?: string;
    } = {},
  ): Promise<{ sessions: SessionListItem[]; total: number }> {
    const params = new URLSearchParams({ site_id: siteId });
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    if (opts.modality) params.set('modality', opts.modality);
    if (opts.status) params.set('status', opts.status);
    if (opts.oa_q && opts.oa_q.trim().length > 0) params.set('oa_q', opts.oa_q.trim());
    return request(`/v1/pilots?${params.toString()}`);
  },

  // ─── Aulas (Fase 1) ──────────────────────────────────────────────────────
  //
  // El backend crea N pilots en un solo request y devuelve un link firmado
  // por alumno. La profe copia el link y se lo pasa al alumno (WhatsApp,
  // email, etc.); no tenemos envío de mail integrado en Fase 1.

  getClassroomRoster(levelIdOrName: string): Promise<{ learners: RosterLearner[] }> {
    // Preferimos level_id exacto (hex 24) para desambiguar cursos con mismo
    // name (ej. "3rd Grade — North" vs "— South"). Si el caller aún pasa
    // name legacy, cae al querystring `level_name`.
    const isId = /^[0-9a-f]{24}$/i.test(levelIdOrName);
    const params = new URLSearchParams(
      isId ? { level_id: levelIdOrName } : { level_name: levelIdOrName },
    );
    return request(`/v1/classrooms/roster?${params.toString()}`);
  },

  createClassroom(input: CreateClassroomInput): Promise<CreateClassroomResponse> {
    return request('/v1/classrooms', { method: 'POST', json: input });
  },

  // Streaming NDJSON — mismo endpoint, header Accept diferente. Emite eventos
  // {kind: 'phase'|'matrix_ready'|'student_created'|'complete'|'error'} para
  // que el modal muestre progreso en vivo (60 alumnos toman ~7-8s serial).
  async createClassroomStreaming(
    input: CreateClassroomInput,
    onEvent: (evt: ClassroomStreamEvent) => void,
  ): Promise<CreateClassroomResponse> {
    const headers = new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
    });
    if (linkTokenProvider) {
      const t = linkTokenProvider();
      if (t) headers.set('X-Link-Token', t);
    }
    if (accessTokenProvider) {
      const t = await accessTokenProvider();
      if (t) headers.set('Authorization', `Bearer ${t}`);
    }
    if (siteIdProvider) {
      const sid = siteIdProvider();
      if (sid !== null) headers.set('X-Site-Id', sid);
    }
    const resp = await fetch('/v1/classrooms', {
      method: 'POST',
      headers,
      body: JSON.stringify(input),
    });
    if (!resp.ok || !resp.body) {
      // Fallback al parsing tradicional para 4xx antes de que empiece el stream
      // (validación Zod, RBAC): responden JSON con problem+json.
      const text = await resp.text();
      let parsed: unknown = null;
      try { parsed = text ? JSON.parse(text) : null; } catch { /* no JSON */ }
      const err = parsed as { detail?: string; title?: string } | null;
      throw new ApiError(err?.detail || err?.title || resp.statusText, resp.status, null);
    }
    const reader = resp.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = '';
    let complete: CreateClassroomResponse | null = null;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      let idx: number;
      while ((idx = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 1);
        if (!line) continue;
        let evt: ClassroomStreamEvent;
        try {
          evt = JSON.parse(line) as ClassroomStreamEvent;
        } catch {
          continue;
        }
        onEvent(evt);
        if (evt.kind === 'complete') {
          complete = {
            classroom_id: evt.classroom_id,
            name: evt.name,
            students: evt.students,
          };
        } else if (evt.kind === 'error') {
          throw new ApiError(evt.message, 500, null);
        }
      }
    }
    if (!complete) throw new ApiError('Stream terminó sin evento complete', 500, null);
    return complete;
  },

  getClassroom(id: string): Promise<ClassroomDetail> {
    return request(`/v1/classrooms/${id}`);
  },

  listClassrooms(opts: { limit?: number; offset?: number; closed?: boolean } = {}): Promise<{
    classrooms: ClassroomListItem[];
    total: number;
  }> {
    const p = new URLSearchParams();
    if (opts.limit !== undefined) p.set('limit', String(opts.limit));
    if (opts.offset !== undefined) p.set('offset', String(opts.offset));
    if (opts.closed !== undefined) p.set('closed', String(opts.closed));
    const qs = p.toString();
    return request(`/v1/classrooms${qs ? `?${qs}` : ''}`);
  },

  getClassroomLive(id: string): Promise<ClassroomLive> {
    return request(`/v1/classrooms/${id}/live`);
  },

  /**
   * Fase B — LLM on-demand del alumno con flag activo. Backend cachea 5min
   * por pilot; llamadas repetidas son gratis.
   */
  getAttentionInsight(
    classroomId: string,
    pilotId: string,
  ): Promise<{
    text: string;
    generated_at: string;
    from_cache: boolean;
    engine_version: string;
  }> {
    return request(
      `/v1/classrooms/${classroomId}/pilots/${pilotId}/attention-detail`,
    );
  },

  /**
   * Fase C — analytics agregado del aula. Cache 30 min, threshold ≥3 dictámenes
   * cerrados. Devuelve `status: 'ready' | 'not_enough_data'` según el estado.
   */
  getClassroomAnalytics(classroomId: string): Promise<ClassroomAnalyticsResponse> {
    return request(`/v1/classrooms/${classroomId}/analytics`);
  },
  regenerateClassroomAnalytics(classroomId: string): Promise<ClassroomAnalyticsResponse> {
    return request(`/v1/classrooms/${classroomId}/analytics/regenerate`, {
      method: 'POST',
    });
  },

  closeClassroom(id: string): Promise<{
    classroom: ClassroomDetail['classroom'];
    already_closed: boolean;
  }> {
    return request(`/v1/classrooms/${id}/close`, { method: 'POST' });
  },

  regenerateClassroomInvite(
    id: string,
    pilotId: string,
  ): Promise<{
    email: string;
    name: string | null;
    pilot_id: string;
    invite_link: string;
    invite_expires_at: string;
  }> {
    return request(`/v1/classrooms/${id}/pilots/${pilotId}/regenerate-invite`, {
      method: 'POST',
    });
  },

  // -----------------------------------------------------------------------
  // Guía remedial + reintento (feature 0003).
  // GET dispara lazy-generate en el backend — la primera llamada tarda 2-4s
  // (LLM+PDF). Llamadas siguientes son instantáneas.
  // -----------------------------------------------------------------------
  getRemedialTask(sessionId: string): Promise<RemedialTaskResponse> {
    return request(`/v1/remedial/tasks/${sessionId}`);
  },

  /**
   * URL de descarga firmada, pedida al momento del clic. El SAS dura 10 min:
   * la que viene en el estado de la tarea ya venció si el alumno estuvo
   * trabajando antes de descargar.
   */
  async remedialGuideUrl(sessionId: string): Promise<{ url: string }> {
    return request(`/v1/remedial/tasks/${sessionId}/guide-url`);
  },
  getRemedialUploadUrl(
    sessionId: string,
    args: { mime: RemedialUploadMime; size_bytes: number },
  ): Promise<{ upload_url: string; blob_ref: string; expires_at: string }> {
    return request(`/v1/remedial/tasks/${sessionId}/upload-url`, {
      method: 'POST',
      json: args,
    });
  },
  confirmRemedialUpload(sessionId: string, blob_ref: string): Promise<RemedialTaskResponse> {
    return request(`/v1/remedial/tasks/${sessionId}/confirm-upload`, {
      method: 'POST',
      json: { blob_ref },
    });
  },
  getRemedialHistory(sessionId: string): Promise<{ rounds: RemedialTaskResponse[] }> {
    return request(`/v1/remedial/tasks/${sessionId}/history`);
  },
  // PUT directo a Azure Blob con el SAS URL emitido por el backend. Fuera
  // del `request()` wrapper porque el URL es completo (host Azure), sin
  // Authorization ni X-Site-Id.
  async putRemedialUploadToBlob(url: string, mime: string, blob: Blob): Promise<void> {
    const resp = await fetch(url, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': mime,
      },
      body: blob,
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`Azure Blob PUT falló ${resp.status}: ${text.slice(0, 200)}`);
    }
  },

  // -----------------------------------------------------------------------
  // Material didáctico del profe (PPT/Word/PDF/imagen). Alimenta las
  // preguntas para que se centren en lo que se enseñó.
  // -----------------------------------------------------------------------
  getLessonMaterialUploadUrl(args: {
    mime: LessonMaterialMime;
    size_bytes: number;
    filename: string;
  }): Promise<LessonMaterialUploadUrlResponse> {
    return request('/v1/lesson-material/upload-url', {
      method: 'POST',
      json: args,
    });
  },
  async putLessonMaterialToBlob(url: string, mime: string, blob: Blob): Promise<void> {
    const resp = await fetch(url, {
      method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': mime },
      body: blob,
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`Azure Blob PUT falló ${resp.status}: ${text.slice(0, 200)}`);
    }
  },
  previewLessonMaterial(
    args: LessonMaterialPreviewRequest,
  ): Promise<LessonMaterialPreviewResponse> {
    return request('/v1/lesson-material/preview', {
      method: 'POST',
      json: args,
    });
  },
};


function buildAuditQuery(opts: AuditQueryOpts & { limit?: number; cursor?: number }): string {
  const p = new URLSearchParams();
  if (opts.hours !== undefined) p.set('hours', String(opts.hours));
  if (opts.db_type) p.set('db_type', opts.db_type);
  if (opts.http_url) p.set('http_url', opts.http_url);
  if (opts.user_mongo_id) p.set('user_mongo_id', opts.user_mongo_id);
  if (opts.namespace) p.set('namespace', opts.namespace);
  if (opts.min_latency_ms !== undefined) p.set('min_latency_ms', String(opts.min_latency_ms));
  if (opts.success_only !== undefined) p.set('success_only', String(opts.success_only));
  if (opts.limit !== undefined) p.set('limit', String(opts.limit));
  if (opts.cursor !== undefined) p.set('cursor', String(opts.cursor));
  return p.toString();
}
